/**
 * Pictures converted as they are added (R144, MB 2 Nov 2026: "every image input accepts .bmp files"). A Windows
 * bitmap (`.bmp`, `.dib`) is taken wherever a picture is chosen: project photos (Teslyn Field, Teslyn Cloud, the
 * phone), document pictures, delivery dockets, JSA pictures, an aircraft's own photo and the weekly report's client
 * logo. It is kept as a PNG, pixel for pixel: Teslyn Cloud's buckets and tables take JPEG, PNG and WebP; the reports'
 * Typst (0.15) embeds PNG, JPEG, GIF and WebP only; the document reader takes no BMP. The person sees "strip.bmp"
 * become "strip.png". Teslyn Field converts in its core (`image` crate), Teslyn Cloud in the browser (a canvas), the
 * phone as it converts HEIC (to JPEG, `phonePhotoHandling`).
 */

/** The extensions of a Windows bitmap. */
export const BITMAP_EXTENSIONS: readonly string[] = ['bmp', 'dib'];

/** What a bitmap is kept as. */
export const IMPORTED_BITMAP_EXT = 'png';

/** Whether a (lower- or upper-case) extension is a bitmap's. */
export function isBitmapExtension(ext: string): boolean {
  return BITMAP_EXTENSIONS.includes(ext.toLowerCase());
}

/** The extension a chosen picture is kept under: a bitmap becomes `png`; anything else is itself, lower case. */
export function importedImageExt(ext: string): string {
  const e = ext.toLowerCase();
  return isBitmapExtension(e) ? IMPORTED_BITMAP_EXT : e;
}

/** The file name a chosen picture is kept under: "strip.bmp" → "strip.png"; other names unchanged (no folder). */
export function importedImageName(fileName: string): string {
  const base = fileName.split(/[\\/]/).pop() ?? fileName;
  const dot = base.lastIndexOf('.');
  if (dot <= 0) return base;
  const ext = base.slice(dot + 1);
  return isBitmapExtension(ext) ? `${base.slice(0, dot)}.${IMPORTED_BITMAP_EXT}` : base;
}
